import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AnswerStatus,
    LearningPracticeQuizResponse,
    PracticeQuizStatus,
    QuizAnswerRequest,
    QuizAnswerResponse,
    QuizNotification,
    QuizPracticeFinishResponse,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/practiceQuiz.api.ts';
import {
    getApiPracticeQuizData,
    postApiPracticeQuizAnswer,
    postApiPracticeQuizFinish,
    postApiPracticeQuizStart,
    postResetPracticeQuizData,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/practiceQuiz.api.ts';
import { COMMON_QUIZ_STATUS } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/constants.ts';
import {
    applyQuizPayload,
    removeQuestionFromSkippedOnes,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/helpers.ts';
import { extractError } from '@utils/slice.ts';

export interface LearningPracticeQuizSlice {
    quizData: LearningPracticeQuizResponse | null;
    error: Record<string, any> | null;
    skippedQuestions: Record<number, number[]>;
    currentQuestionId: number | null;
    isAnyAnswerProvided: boolean;
    checkedAnswers: Record<number, number[]>;
    correctAnswers: Record<number, number[]>;
    answeredQuestions: number[];
    quizQuestionAnswerStatuses: Record<number, AnswerStatus>;
    quizFinishData: QuizPracticeFinishResponse | null;
    currentPracticeQuizStatus: PracticeQuizStatus | undefined;
    quizNotification: QuizNotification | undefined;
    showSkipQuestionsNumbersBanner: boolean;

    isFetchPracticeQuizDataPending: boolean;
    isStartPracticeQuizPending: boolean;
    isPostAnswerPracticeQuizPending: boolean;
    isFinishPracticeQuizPending: boolean;
    isResetPracticeQuizDataPending: boolean;
}

const initialState: LearningPracticeQuizSlice = {
    quizData: null,
    error: null,
    skippedQuestions: {},
    currentQuestionId: null,
    isAnyAnswerProvided: false,
    checkedAnswers: {},
    correctAnswers: {},
    answeredQuestions: [],
    quizQuestionAnswerStatuses: {},
    quizFinishData: null,
    currentPracticeQuizStatus: undefined,
    quizNotification: undefined,
    showSkipQuestionsNumbersBanner: false,

    isFetchPracticeQuizDataPending: true,
    isStartPracticeQuizPending: false,
    isPostAnswerPracticeQuizPending: false,
    isFinishPracticeQuizPending: false,
    isResetPracticeQuizDataPending: false,
};

export const fetchPracticeQuizData = createAsyncThunk<
    LearningPracticeQuizResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningPracticeQuiz/fetchPracticeQuizData', ({ pathwayId, quizId }, { rejectWithValue }) =>
    getApiPracticeQuizData(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const startPracticeQuiz = createAsyncThunk<
    LearningPracticeQuizResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningPracticeQuiz/startPracticeQuiz', ({ pathwayId, quizId }, { rejectWithValue }) =>
    postApiPracticeQuizStart(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const postAnswerPracticeQuiz = createAsyncThunk<
    QuizAnswerResponse,
    { payload: QuizAnswerRequest; quizId: number; questionId: number; pathwayId: string },
    { rejectValue: any }
>('learningPracticeQuiz/postApiPracticeQuizAnswer', ({ payload, quizId, questionId, pathwayId }, { rejectWithValue }) =>
    postApiPracticeQuizAnswer(payload, quizId, questionId, pathwayId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const finishPracticeQuiz = createAsyncThunk<
    QuizPracticeFinishResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningPracticeQuiz/postApiPracticeQuizFinish', ({ pathwayId, quizId }, { rejectWithValue }) =>
    postApiPracticeQuizFinish(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const resetPracticeQuizData = createAsyncThunk<
    LearningPracticeQuizResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningPracticeQuiz/postResetPracticeQuizData', ({ pathwayId, quizId }, { rejectWithValue }) =>
    postResetPracticeQuizData(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const learningPracticeQuizSlice = createSlice({
    name: 'learningPracticeQuiz',
    initialState,
    reducers: {
        setSkippedQuestions(state, action: PayloadAction<{ quizId: number; questionId: number }>) {
            const { quizId, questionId } = action.payload;
            const list = state.skippedQuestions[quizId] ?? (state.skippedQuestions[quizId] = []);
            if (!list.includes(questionId)) {
                list.push(questionId);
                state.showSkipQuestionsNumbersBanner = true;
            }
        },
        clearAllSkippedQuestions(state) {
            state.skippedQuestions = {};
        },
        setIsAnyAnswerProvided(state, action: PayloadAction<boolean>) {
            state.isAnyAnswerProvided = action.payload;
        },
        setCurrentQuestionId(state, action: PayloadAction<number | null>) {
            state.currentQuestionId = action.payload;
        },
        setCheckedAnswers(state, action: PayloadAction<{ questionId: number; answers: number[] }>) {
            const { questionId, answers } = action.payload;
            state.checkedAnswers[questionId] = answers;
        },
        showSkipQuestionsNumbersBanner(state, action: PayloadAction<boolean>) {
            state.showSkipQuestionsNumbersBanner = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPracticeQuizData.pending, (state) => {
            state.error = null;
            state.isFetchPracticeQuizDataPending = true;
            state.currentPracticeQuizStatus = undefined;
            state.quizFinishData = null;
        });
        builder.addCase(fetchPracticeQuizData.fulfilled, (state, action) => {
            applyQuizPayload(state, action.payload);
            state.currentPracticeQuizStatus = action.payload.quizStatus;
            state.isFetchPracticeQuizDataPending = false;

            const { quizStatus, requiredPercentScore, currentPercentScore, highestPercentScore } = action.payload;

            if (
                (quizStatus === COMMON_QUIZ_STATUS.PASSED || quizStatus === COMMON_QUIZ_STATUS.FAILED) &&
                currentPercentScore !== null &&
                highestPercentScore !== null
            ) {
                state.quizFinishData = {
                    requiredPercentScore,
                    currentPercentScore,
                    highestPercentScore,
                    quizStatus,
                    lastQuizFinished: true,
                };
            }
        });
        builder.addCase(fetchPracticeQuizData.rejected, (state, action) => {
            state.error = extractError(action);
            state.isFetchPracticeQuizDataPending = false;
        });

        builder.addCase(startPracticeQuiz.pending, (state) => {
            state.error = null;
            state.isStartPracticeQuizPending = true;
            state.currentPracticeQuizStatus = undefined;
        });
        builder.addCase(startPracticeQuiz.fulfilled, (state, action) => {
            applyQuizPayload(state, action.payload);
            state.currentPracticeQuizStatus = action.payload.quizStatus;
            state.isStartPracticeQuizPending = false;
        });
        builder.addCase(startPracticeQuiz.rejected, (state, action) => {
            state.error = extractError(action);
            state.isStartPracticeQuizPending = false;
        });

        builder.addCase(postAnswerPracticeQuiz.pending, (state) => {
            state.error = null;
            state.isPostAnswerPracticeQuizPending = true;
        });
        builder.addCase(postAnswerPracticeQuiz.fulfilled, (state, action) => {
            const questionId = action.meta.arg.questionId;
            const selected = action.meta.arg.payload.answers;

            // subset of user's selected that were correct
            state.correctAnswers[questionId] = action.payload.correct ?? [];
            state.checkedAnswers[questionId] = selected;

            // immediate status from the answer response
            state.quizQuestionAnswerStatuses[questionId] = action.payload.quizQuestionAnswerStatus;

            if (!state.answeredQuestions.includes(questionId)) {
                state.answeredQuestions.push(questionId);
            }

            const quizIdFromState = (state.quizData as any)?.id as number | undefined;
            removeQuestionFromSkippedOnes(state, questionId, quizIdFromState);

            state.isAnyAnswerProvided = false;
            state.showSkipQuestionsNumbersBanner = false;
            state.isPostAnswerPracticeQuizPending = false;
        });
        builder.addCase(postAnswerPracticeQuiz.rejected, (state, action) => {
            state.error = extractError(action);
            state.isPostAnswerPracticeQuizPending = false;
        });

        builder.addCase(finishPracticeQuiz.pending, (state) => {
            state.error = null;
            state.isFinishPracticeQuizPending = true;
        });
        builder.addCase(finishPracticeQuiz.fulfilled, (state, action) => {
            state.quizFinishData = action.payload;
            state.error = null;
            state.currentPracticeQuizStatus = action.payload.quizStatus;
            state.isFinishPracticeQuizPending = false;
        });
        builder.addCase(finishPracticeQuiz.rejected, (state, action) => {
            state.error = extractError(action);
            state.isFinishPracticeQuizPending = false;
        });

        builder.addCase(resetPracticeQuizData.pending, (state) => {
            state.error = null;
            state.isResetPracticeQuizDataPending = true;
        });
        builder.addCase(resetPracticeQuizData.fulfilled, (state) => {
            state.isResetPracticeQuizDataPending = false;
        });
        builder.addCase(resetPracticeQuizData.rejected, (state, action) => {
            state.error = action.payload ?? action.error.message ?? null;
            state.isResetPracticeQuizDataPending = false;
        });
    },
});

export const {
    setSkippedQuestions,
    setIsAnyAnswerProvided,
    setCurrentQuestionId,
    setCheckedAnswers,
    clearAllSkippedQuestions,
    showSkipQuestionsNumbersBanner,
} = learningPracticeQuizSlice.actions;
export default learningPracticeQuizSlice.reducer;
